import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export const FilterSection = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 py-4">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left group">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{icon}</span>
          <span className="font-semibold text-gray-800">{title}</span>
        </div>
        <div className={`w-6 h-6 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-4 w-4 text-gray-600" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>{children}</div>
    </div>
  );
};

export const CheckboxItem = ({ label, count, checked, onChange, logo, testid }) => (
  <label className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-gray-50 px-3 rounded-lg transition-colors group" data-testid={testid}>
    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${checked ? 'bg-[#1a5c3b] border-[#1a5c3b] scale-110' : 'border-gray-300 group-hover:border-gray-400'}`}>
      {checked && <Check className="h-3 w-3 text-white" />}
    </div>
    {logo && <img src={logo} alt="" className="h-6 w-6 object-contain shrink-0 bg-gray-100 rounded p-0.5" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
    <span className={`flex-1 transition-colors ${checked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{label}</span>
    <span className={`text-sm px-2 py-0.5 rounded-full transition-colors ${checked ? 'bg-[#1a5c3b] text-white' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
    <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
  </label>
);

export const ScrollableList = ({ children, deps }) => {
  const ref = useRef(null);
  const [hint, setHint] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setHint(el.scrollHeight > el.clientHeight + 4 && el.scrollTop + el.clientHeight < el.scrollHeight - 16);
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [deps]);
  return (
    <div className="relative">
      <div ref={ref} className="max-h-96 overflow-y-auto space-y-0.5 pr-1 custom-scrollbar" data-testid="makes-scroll-area">{children}</div>
      <div className={`pointer-events-none absolute left-0 right-1 bottom-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent transition-opacity ${hint ? 'opacity-100' : 'opacity-0'}`} />
      <button
        type="button"
        onClick={() => ref.current?.scrollBy({ top: 160, behavior: 'smooth' })}
        aria-label="More"
        data-testid="makes-scroll-hint"
        className={`absolute left-1/2 -translate-x-1/2 bottom-1 w-7 h-7 rounded-full bg-[#1a5c3b] text-white flex items-center justify-center shadow-md transition-all hover:scale-110 ${hint ? 'opacity-100 animate-bounce' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
};
